import styled, { css, keyframes } from 'styled-components';
import { transparentize } from 'polished';
import {
  brand,
  breakpoints,
  surface,
  touchFeedback,
  touchTarget,
  textSize,
  displayType,
  ink,
  tones,
} from '../../../styles/brand';

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.15; }
`;

const fastBlink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

export const Container = styled.div`
  ${touchFeedback}

  width: 100%;
  max-width: 44.44rem;
  margin: 0 auto;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .primary-button {
    height: 1.8rem;
    padding: 0.8rem;
    font-family: 'RedHatDisplay-Bold';
    font-size: ${textSize(1)};
    border-radius: 0.6rem;
    box-shadow: 0px 3px 15px rgba(255, 152, 0, 0.3);

    > svg {
      margin-right: 0.3rem;
    }
  }

  .secondary-button {
    height: 1.8rem;
    padding: 0.8rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: ${textSize(1)};
    border-radius: 0.6rem;
    vertical-align: center;
  }

  /* Celular: o juiz avalia em pé, com uma mão. Alvos de 44pt e espaço para a
     barra fixa de envio no rodapé. */
  @media (max-width: ${breakpoints.md}) {
    padding: 1rem;
    padding-bottom: calc(6rem + env(safe-area-inset-bottom, 0px));

    .primary-button,
    .secondary-button {
      height: ${touchTarget};
      touch-action: manipulation;
    }
  }
`;

export const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  border-bottom: 1px solid ${surface.line};

  h1 {
    font-size: ${textSize(1.5)};
    ${displayType}
    color: ${brand.blue};
    font-family: 'RedHatDisplay-ExtraBold';
  }

  @media (max-width: ${breakpoints.md}) {
    h1 {
      font-size: ${textSize(1.15)};
      line-height: 1.3;
    }
  }
`;

export const MainCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid ${surface.line};
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;

  @media (max-width: ${breakpoints.md}) {
    padding: 1rem;
  }
`;

export const StopwatchContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;

  h3 {
    font-size: ${textSize(1)};
    color: ${ink};
    font-weight: 600;
  }
`;

export const StopwatchDisplay = styled.div<{
  $alertLevel?: 'normal' | 'warning' | 'critical' | 'ended';
}>`
  font-variant-numeric: tabular-nums;
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;

  .time {
    font-size: ${textSize(2.5)};
    font-family: 'RedHatDisplay-Bold';
    padding: 0.5rem 1rem;
    border-radius: 8px;
    width: 10.2rem;
    text-align: center;

    transition:
      color 0.3s,
      border-color 0.3s,
      background 0.3s;

    color: ${({ $alertLevel }) =>
      $alertLevel === 'ended' || $alertLevel === 'critical'
        ? tones.red.text
        : $alertLevel === 'warning'
          ? '#e65100'
          : ink};

    border: 2px solid
      ${({ $alertLevel }) =>
        $alertLevel === 'ended' || $alertLevel === 'critical'
          ? '#dc3545'
          : $alertLevel === 'warning'
            ? brand.orange
            : surface.line};

    background: ${({ $alertLevel }) =>
      $alertLevel === 'ended' || $alertLevel === 'critical'
        ? tones.red.soft
        : $alertLevel === 'warning'
          ? '#fff9f0'
          : '#fff'};

    ${({ $alertLevel }) =>
      $alertLevel === 'warning' &&
      css`
        animation: ${blink} 1s ease-in-out infinite;
      `}

    ${({ $alertLevel }) =>
      $alertLevel === 'critical' &&
      css`
        animation: ${fastBlink} 0.4s ease-in-out infinite;
      `}

    /* Piscar rápido incomoda e pode causar mal-estar: a cor já avisa. */
    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }

  @media (max-width: ${breakpoints.md}) {
    flex-wrap: wrap;
    gap: 0.5rem;

    .time {
      width: 100%;
      font-size: ${textSize(3)};
    }

    .primary-button,
    .secondary-button {
      flex: 1;
    }
  }
`;

export const AlertBadge = styled.div<{
  $level: 'warning' | 'critical' | 'ended';
}>`
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.9rem;
  border-radius: 20px;
  font-size: ${textSize(0.82)};
  font-weight: 700;
  margin-top: 0.25rem;

  ${({ $level }) =>
    $level === 'warning' &&
    css`
      background: #fff3cd;
      color: #7d4e00;
      border: 1px solid #ffc107;
    `}

  ${({ $level }) =>
    ($level === 'critical' || $level === 'ended') &&
    css`
      background: ${tones.red.soft};
      color: ${tones.red.text};
      border: 1px solid ${tones.red.border};
    `}
`;

export const MissionRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.6rem;
`;

export const MissionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  h4 {
    font-size: ${textSize(1)};
    color: ${ink};
    font-weight: 600;
  }

  span {
    color: ${brand.blue};
    font-weight: 600;
  }
`;

export const OptionsGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;

  /* Celular: as opções de uma missão ficam lado a lado, como um controle
     segmentado. Com 3 opções cabe tudo numa linha; com 5 (Robô Pintor),
     quebra em 3 + 2 sem deixar uma sozinha embaixo de duas. */
  @media (max-width: ${breakpoints.md}) {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(6rem, 1fr));
  }
`;

export const OptionCard = styled.button<{
  $selected: boolean;
  $isZero?: boolean;
}>`
  flex: 1;
  min-width: 120px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid
    ${({ $selected }) => ($selected ? 'transparent' : surface.line)};
  background: ${({ $selected, $isZero }) =>
    $selected ? ($isZero ? tones.red.soft : tones.green.soft) : '#fff'};
  cursor: pointer;
  transition:
    background-color 0.16s ease,
    border-color 0.16s ease;
  text-align: left;
  touch-action: manipulation;

  @media (max-width: ${breakpoints.md}) {
    min-width: 0;
    min-height: 3.25rem;
  }

  span.label {
    font-size: ${textSize(0.85)};
    color: ${({ $selected }) =>
      $selected ? tones.green.text : tones.gray.text};
    font-weight: ${({ $selected }) => ($selected ? '600' : '500')};
    ${({ $selected, $isZero }) =>
      $selected &&
      $isZero &&
      css`
        color: ${tones.red.text};
      `}
  }

  span.points {
    font-size: ${textSize(0.8)};
    color: ${({ $selected }) =>
      $selected ? tones.green.text : tones.gray.text};
    font-weight: 600;
    margin-top: 0.25rem;
    ${({ $selected, $isZero }) =>
      $selected &&
      $isZero &&
      css`
        color: ${tones.red.text};
      `}
  }

  @media (hover: hover) {
    &:hover {
      border-color: #a0a0a0;
    }
  }
`;

export const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

export const CustomToggle = styled.button<{ $active: boolean }>`
  width: 50px;
  height: 26px;
  border-radius: 15px;
  background-color: ${({ $active }) => ($active ? '#4cd964' : '#e0e0e0')};
  border: none;
  position: relative;
  cursor: pointer;
  transition: background-color 0.2s;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${({ $active }) => ($active ? '26px' : '2px')};
    width: 22px;
    height: 22px;
    background-color: white;
    border-radius: 50%;
    transition: left 0.2s;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
`;

export const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
  cursor: pointer;
  font-size: ${textSize(1)};
  color: ${ink};

  input[type='checkbox'] {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

export const CounterRow = styled.div`
  font-variant-numeric: tabular-nums;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 1.5rem;
  padding: 1rem;
  border: 1px solid ${tones.red.border};
  border-radius: 8px;
  background: ${tones.red.soft};

  span.label {
    font-size: ${textSize(1)};
    font-weight: 500;
    color: ${ink};
  }

  div.controls {
    display: flex;
    align-items: center;
    gap: 1rem;

    button {
      width: 32px;
      height: 32px;
      touch-action: manipulation;

      @media (max-width: ${breakpoints.md}) {
        width: ${touchTarget};
        height: ${touchTarget};
      }

      display: flex;
      align-items: center;
      justify-content: center;
      border: none;
      background: #e0e0e0;
      border-radius: 4px;
      font-size: ${textSize(1.2)};
      font-weight: bold;
      color: ${ink};
      cursor: pointer;

      &:hover {
        background: #ccc;
      }
    }

    button.orange {
      background: ${brand.orange};
      color: white;

      &:hover {
        background: #f57c00;
      }
    }

    span.value {
      font-size: ${textSize(1.2)};
      font-weight: 600;
      min-width: 1.5rem;
      text-align: center;
    }
  }
`;

export const ScoreSummary = styled.div`
  font-variant-numeric: tabular-nums;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${tones.blue.soft};
  border-radius: 8px;
  padding: 1.5rem;
  margin-top: 1.5rem;

  @media (max-width: ${breakpoints.md}) {
    flex-wrap: wrap;
    gap: 0.75rem;
    padding: 1rem;

    .box {
      flex: 1 1 40%;
    }
  }

  .box {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;

    span.label {
      font-size: ${textSize(0.85)};
      color: ${tones.gray.text};
    }

    span.value {
      font-size: ${textSize(1.5)};
      font-weight: 700;
      color: ${ink};
    }

    span.bonus {
      color: ${tones.green.text};
    }

    span.penalty {
      color: ${tones.red.text};
    }

    span.total {
      color: ${brand.blue};
    }
  }
`;

export const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;

  .primary-button {
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: #fff;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 4px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;

    svg {
      margin-left: 0.5rem;
    }

    &.disabled {
      cursor: not-allowed;
    }
  }

  .primary-button {
    height: 2.8rem;
    font-size: ${textSize(0.9)};
    padding: 0 1.2rem;
    border-radius: 0.55rem;
    display: flex;
    align-items: center;
    gap: 0.4rem;
    box-shadow: 0px 3px 8px rgba(255, 152, 0, 0.3);
  }

  /* Celular: enviar a avaliação fica fixo no rodapé, ao alcance do polegar. */
  @media (max-width: ${breakpoints.md}) {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 3000;
    margin: 0;
    padding: 0.75rem max(1rem, env(safe-area-inset-right, 0px))
      calc(0.75rem + env(safe-area-inset-bottom, 0px))
      max(1rem, env(safe-area-inset-left, 0px));
    border-top: 1px solid ${surface.line};
    background: ${transparentize(0.15, brand.white)};
    backdrop-filter: blur(18px) saturate(180%);
    -webkit-backdrop-filter: blur(18px) saturate(180%);

    .primary-button {
      width: 100%;
      height: ${touchTarget};
      justify-content: center;
      font-size: ${textSize(1)};
    }
  }

  @media (prefers-reduced-transparency: reduce) {
    background: ${brand.white};
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }
`;

export const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

export const ScorecardInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid ${surface.line};
`;

export const ScorecardInfoCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;

  span.label {
    font-size: ${textSize(0.75)};
    color: ${tones.gray.text};
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  span.value {
    font-size: ${textSize(0.95)};
    font-weight: 600;
    color: ${ink};
  }
`;

export const ScorecardMissionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-top: 0.5rem;
`;

export const ScorecardMissionItem = styled.div<{
  $score: number;
  $maxScore: number;
}>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.6rem 1rem;
  border-radius: 8px;
  background: ${({ $score, $maxScore }) =>
    $score === $maxScore
      ? tones.green.soft
      : $score === 0
        ? tones.red.soft
        : '#fff3cd'};
  border: 1px solid
    ${({ $score, $maxScore }) =>
      $score === $maxScore
        ? tones.green.border
        : $score === 0
          ? tones.red.border
          : '#ffeeba'};

  span.name {
    font-size: ${textSize(0.88)};
    color: ${ink};
  }

  span.score {
    font-size: ${textSize(0.9)};
    font-weight: 700;
    color: ${({ $score, $maxScore }) =>
      $score === $maxScore
        ? tones.green.text
        : $score === 0
          ? tones.red.text
          : '#7d4e00'};
  }
`;

export const ScorecardBonusPill = styled.div<{ $applied: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.9rem;
  border-radius: 20px;
  font-size: ${textSize(0.82)};
  font-weight: 600;
  background: ${({ $applied }) => ($applied ? tones.green.soft : surface.page)};
  border: 1px solid
    ${({ $applied }) => ($applied ? tones.green.border : surface.line)};
  color: ${({ $applied }) => ($applied ? tones.green.text : tones.gray.text)};
`;

export const ScorecardMembersSection = styled.div`
  margin-top: 0.5rem;
  border-top: 1px solid ${surface.line};
  padding-top: 1rem;

  h4 {
    font-size: ${textSize(0.9)};
    font-weight: 600;
    color: ${tones.gray.text};
    margin-bottom: 0.5rem;
  }

  ul {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
  }

  li {
    font-size: ${textSize(0.85)};
    color: ${ink};
  }
`;

export const ScorecardNavRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
`;
